'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCode, MdOpenInNew, MdLink, MdVolumeUp, MdClose } from 'react-icons/md';
import { SiReact, SiRedux, SiFirebase, SiTailwindcss, SiReactrouter } from 'react-icons/si';
import { MdSmartToy } from 'react-icons/md';
import { Carousel, Card } from './ui/apple-cards-carousel';
import leftAvatar from '../assets/left side.png'
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createPortal } from 'react-dom';
import noaiiii from '../assets/noaii.png'
// Images
import Astro1 from '../assets/projs/Astro1.jpg'
import Astro2 from '../assets/projs/Astro2.jpg'
import Astro3 from '../assets/projs/Astro3.jpg'
import Astro4 from '../assets/projs/Astro4.jpg'
import Astro5 from '../assets/projs/Astro5.jpg'
import Astro6 from '../assets/projs/Astro6.jpg'

import Food1 from '../assets/projs/food1.jpg'
import Food3 from '../assets/projs/food3.jpg'
import Food2 from '../assets/projs/Food2.jpg'

import Hot1 from '../assets/projs/Hot1.jpg'
import Hot2 from '../assets/projs/Hot2.jpg'
import Hot3 from '../assets/projs/Hot3.jpg'

gsap.registerPlugin(ScrollTrigger);

// Video Modal Component
const VideoModal = ({ videoUrl, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      return () => {
        document.body.style.overflow = "unset";
        document.body.style.paddingRight = "0px";
      };
    }
  }, [isOpen]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-2 sm:p-4 overflow-hidden"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative w-full max-w-sm sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] bg-black rounded-lg overflow-y-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="sticky top-3 right-3 sm:top-4 sm:right-4 z-50 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all ml-auto"
              >
                <MdClose size={24} />
              </button>
              <div className="w-full aspect-video flex-shrink-0 bg-black">
                <iframe
                  width="100%"
                  height="100%"
                  src={videoUrl.replace('youtu.be/', 'www.youtube.com/embed/').split('?')[0] + '?autoplay=1&mute=1'}
                  title="Project Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Image Carousel for Card
const CardImageCarousel = ({ images, onPlayClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Image */}
      <img
        src={images[currentIndex]}
        alt="project"
        className="w-full h-full object-cover"
      />

      {/* Top Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-transparent z-10" />

      {/* Speaker Icon - appears on hover */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        onClick={onPlayClick}
        className="absolute top-4 right-4 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all opacity-0 hover:opacity-100"
      >
        <MdVolumeUp size={24} />
      </motion.button>

      {/* Carousel Dots at bottom right */}
      <div className="absolute bottom-4 right-4 z-20 flex gap-1.5">
        {images.map((_, idx) => (
          <motion.button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`h-2 rounded-full transition-all ${idx === currentIndex
              ? 'bg-white w-6'
              : 'bg-white/40 w-2 hover:bg-white/60'
              }`}
            whileHover={{ scale: 1.2 }}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectContent = ({ project }) => {
  return (
    <div className="bg-[#F5F5F7] dark:bg-neutral-800 p-6 md:p-10 rounded-2xl mb-4">
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="text-lg font-bold text-neutral-700 dark:text-neutral-200 mb-2">
            Overview
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base font-sans max-w-3xl leading-relaxed">
            {project.shortDescription}
          </p>
        </div>

        <div>
          <h3 className="text-lg font-bold text-neutral-700 dark:text-neutral-200 mb-3">
            Tech Stack
          </h3>
          <div className="flex flex-wrap gap-2">
            {project.techStack.map((tech, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 rounded-full shadow-sm"
              >
                <span className="text-lg text-blue-500">{tech.icon}</span>
                <span className="text-xs font-medium dark:text-zinc-300">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold text-neutral-700 dark:text-neutral-200 mb-3">
            Details
          </h3>
          <div className="text-neutral-600 dark:text-neutral-400 text-sm md:text-sm font-sans space-y-3">
            {project.fullDescription.split('\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-4">
          <a
            href={project.links.github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-black text-white px-3 py-1.5 rounded-full font-medium hover:bg-neutral-800 transition-all shadow-lg text-xs md:text-sm"
          >
            <MdCode size={18} />
            GitHub
          </a>
          <a
            href={project.links.live}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-full font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 text-xs md:text-sm"
          >
            <MdOpenInNew size={16} />
            Live Demo
          </a>
          <a
            href={project.links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white dark:bg-neutral-900 text-black dark:text-white border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 rounded-full font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all shadow-sm text-xs md:text-sm"
          >
            <MdLink size={18} />
            Case Study
          </a>
        </div>
      </div>
    </div>
  );
};

const Project = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const projectData = [
    {
      id: 1,
      title: "AstroGPT",
      category: "Astrology & AI",
      src: Astro1,
      images: [Astro1, Astro2, Astro3, Astro4, Astro5, Astro6],
      video: "https://youtu.be/7JK6egMNLWA",
      icon: <MdSmartToy className="text-white" />,
      shortDescription: "A revolutionary AI astrology platform featuring real-time AstroTalk API integration, personalized Kundli, and an intelligent ChatGPT-powered consultation bot.",
      fullDescription: "AstroGPT is a high-performance astrology application enriched with ChatGPT capabilities. It features real-time AstroTalk API integration, personalized Kundli, daily horoscopes, and multi-language support. Built for scalability and responsiveness.",
      links: {
        github: "https://github.com/VKoder/AstroGPT",
        live: "https://astro-gpt-eta.vercel.app/",
        linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7182353392160129026/",
      },
      techStack: [
        { icon: <SiReact />, name: "React" },
        { icon: <SiRedux />, name: "Redux Toolkit" },
        { icon: <SiFirebase />, name: "Firebase" },
        { icon: <MdSmartToy />, name: "ChatGPT" },
        { icon: <SiTailwindcss />, name: "Tailwind CSS" },
        { icon: <SiReactrouter />, name: "React Router" },
      ],
    },
    {
      id: 2,
      title: "Food Bazaar",
      category: "Food Delivery",
      src: Food1,
      images: [Food1, Food2, Food3],
      video: "https://youtu.be/EP2XRgYtV4E",
      icon: <SiReact className="text-white" />,
      shortDescription: "A high-performance food delivery ecosystem integrating live Swiggy and Instamart APIs with a polished shimmer UI and optimized state management.",
      fullDescription: "Food Bazaar is a comprehensive food delivery platform. Key features include real-time Swiggy API integration, lazy loading for performance, custom hooks for reusability, and a polished shimmer UI for better UX.",
      links: {
        github: "https://github.com/VKoder/FoodBazaar-",
        live: "https://www.linkedin.com/feed/update/urn:li:activity:7156157580912820225/",
        linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7156157580912820225/",
      },
      techStack: [
        { icon: <SiReact />, name: "React" },
        { icon: <SiRedux />, name: "Redux Toolkit" },
        { icon: <SiTailwindcss />, name: "Tailwind CSS" },
        { icon: <SiReactrouter />, name: "React Router" },
      ],
    },
    {
      id: 3,
      title: "HotflixGPT",
      category: "Streaming & AI",
      src: Hot1,
      images: [Hot1, Hot2, Hot3],
      video: "https://youtu.be/-b7wLLVU_DQ",
      icon: <SiRedux className="text-white" />,
      shortDescription: "An AI-powered cinema platform merging TMDB intelligence with OpenAI GPT-3.5 to deliver hyper-personalized movie discovery and streaming experiences.",
      fullDescription: "HotflixGPT combines React, Tailwind CSS, Firebase, TMDB, and OpenAI GPT-3.5 turbo APIs. It features intelligent movie searches, infinite scroll, multi-language support, and optimized performance with memoization.",
      links: {
        github: "https://github.com/VKoder/Hotflix-GPT",
        live: "https://hotflix-gpt-xfgn.vercel.app/",
        linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7160622459300282368/",
      },
      techStack: [
        { icon: <SiReact />, name: "React" },
        { icon: <SiRedux />, name: "Redux Toolkit" },
        { icon: <SiFirebase />, name: "Firebase" },
        { icon: <SiTailwindcss />, name: "Tailwind CSS" },
        { icon: <SiReactrouter />, name: "React Router" },
      ],
    },

  ];

  const cards = projectData.map((project, index) => (
    <Card
      key={project.id}
      card={{
        ...project,
        customImage: (
          <CardImageCarousel
            images={project.images}
            onPlayClick={() => {
              setSelectedVideo(project.video);
              setIsVideoModalOpen(true);
            }}
          />
        ),
        content: <ProjectContent project={project} />
      }}
      index={index}
    />
  ));

  useEffect(() => {
    gsap.fromTo(
      '.project-side-avatar',
      {
        opacity: 0,
        scale: 0.5,
        x: 50,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        duration: 1.5,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '#projects',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <>
      <VideoModal
        videoUrl={selectedVideo}
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />
      <div id='projects' className="w-full relative h-full lg:py-12 py-8 overflow-hidden">
        <div className=' w-full h-full'>
          <div
            className="absolute -top-10 -left-10 w-72 h-72 opacity-20 dark:opacity-10 text-muted-foreground"
            style={{
              backgroundImage: `
      linear-gradient(to right, currentColor 1.5px, transparent 1.5px),
      linear-gradient(to bottom, currentColor 1.5px, transparent 1.5px)
    `,
              backgroundSize: "60px 60px"
            }}
          />

        </div>

        {/* <img src={leftAvatar} className='project-side-avatar absolute -right-0 top-0 w-80 md:w-96 h-auto z-0 pointer-events-none' /> */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
          >

            {/* <div className="h-1 w-20 bg-blue-600 rounded-full mb-6" /> */}
            <h2 className="section-title text-zinc-900 dark:text-zinc-100 text-5xl md:text-7xl lg:font-bold font-semibold  mb-2 lg:mb-3 ">
              Early   <span className="bg-clip-text text-transparent bg-gradient-to-r from-zinc-500 to-zinc-500 dark:from-zinc-400 dark:to-zinc-400">Learning Projects</span>
            </h2>
            <div className='flex lg:flex-row flex-col justify-start gap-2 items-center'>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <img src={noaiiii} alt="" className='lg:w-40 !-rotate-120 w-28' />
              </motion.div>
              <p className="max-w-3xl text-lg md:text-xl text-zinc-600 dark:text-zinc-400 lg:mb-8 mb-6 font-light leading-relaxed">
                <span>Please read this it's not any random ai generated content : </span>
                Some scalable production work I’ve done is private and can’t be shared yet. The projects below are simple ones I built while I was just starting and learning the basics. For my more serious, real-world work, please check the recent experience section above.
              </p>

            </div>
          </motion.div>
        </div>

        <Carousel items={cards} autoScroll={false} gap="gap-6 md:gap-8" />
      </div>
    </>
  );
};

export default Project;
